import {ColProps, UploadFile} from "antd";
import {ChangeEvent} from "react";
import {isGenericDateField} from "../utils";
import {FieldDTOPayload, FieldDTOTypes, JFieldDTOType} from "./overrideBE";
import {TooltipDTO} from "@jnext/ts-axios-coreuser";
import {JRadioStyle} from "../../JForm";

export interface ComposedTranslationLabels {
    required: string,
    confirm: string,
    noMatch: string,
    wrongFormat: string,
    invalidDate: string
    invalidPhone: string
}

export interface ComposedTranslationLabelsOptions {
    validationMessage?: string,
    ignoreRequired?: boolean,
    ignoreConfirm?: boolean,
    ignoreNoMatch?: boolean,
    ignoreWrongFormat?: boolean
    ignoreInvalidDate?: boolean,
    ignoreInvalidPhone?: boolean
}

export type FormFieldActionType = "OPEN_CROP" | "CLOSE_CROP"
export interface FormFieldActionEvent {
    type: FormFieldActionType
}

export type OnSubmitForm = (output: FieldDTOPayload, confirmedValues: Output) => Promise<void>

export type OnSubmitRawData = (data: Output, confirms: Output) => any

export type OnSubmitErrorForm = (error: Error) => any
export interface Output {
    [key: string]: string | string[] | moment.Moment | [moment.Moment, moment.Moment] | UploadFile[] | Output
}
export type FormFieldOnAction = (event: FormFieldActionEvent) => void
export interface JFieldBaseProps {
    type: JFieldDTOType;
    name: (string | number)[];
    fullname: (string | number)[];
    fieldKey: string;
    label: string;
    placeholder: string;
    tooltip?: TooltipDTO;
    value?: string | string[];
    overrideValue?: string;
    showLabel?: boolean;
    mandatory?: boolean;
    validationScript?: string | null;
    disabled?: boolean;
    confirmed?: boolean;
    description?: string;
    infoFileURL?: string;
    validationMessage?: string;
    onAction: FormFieldOnAction;
    extraOptions?: Record<string, string>;
    hidden?: boolean;
    autocomplete?: string;
};

export interface JSelectOption {
    value: string;
    label: string;
    svg?: string;
    dependsValue?: string;
}

export interface JValueSelectProps extends JFieldBaseProps {
    dependsOn?: string
    options: JSelectOption[],
    searcheable?: boolean
    disableAllowClear?: boolean,
    mode?: 'tags' | 'multiple'
}

export interface JRadioProps extends Omit<JFieldBaseProps, 'confirmed'> {
    style?: JRadioStyle,
    options: {
        label: string;
        imageURL?: string;
        value: string;
    }[]
}
export interface JCountryProps extends Omit<JValueSelectProps, 'options'> { }

export interface JPrivacyProps extends JRadioProps { }
export interface JCheckboxGroupProps extends JRadioProps {
    value: string[];
}
export type CropRenderMode = 'modal' | 'replace'
export interface JUploadProps extends Omit<JFieldBaseProps, 'confirmed'> {
    value?: string[],
    accept?: string,
    multiple?: boolean,
    maxCount?: number,
    maxSize?: number,
    enableCrop?: boolean,
    icon?: JSX.Element,
    cropRenderMode?: CropRenderMode,
    showPreview?: boolean,
    minimal?: boolean,
    onChange?: (event: UploadFile[] | undefined) => void
}
export interface JAvatarProps extends JUploadProps {
    showAvatarPreview?: boolean,
}
export interface JDateProps extends JFieldBaseProps {
    format?: string,
    showTime?: boolean,
    value?: string,
    minDate?: Date,
    maxDate?: Date,
}
export interface JDateRangeProps extends Omit<JDateProps, 'value'> {
    value?: [string, string]
}

export interface JTextProps extends JFieldBaseProps {
    status?: 'VALID' | 'INVALID',
    htmlType?: JTextHTMLTypes,
    onChange?: (value: ChangeEvent<HTMLInputElement>) => void
}

export interface JNumberProps extends JFieldBaseProps {
    onChange?: (value: any) => void
}

export interface UploadEvent {
    fileList: UploadFile[]
}


export interface FormOutput<TypedOutput extends Output = Output> {
    /**
     * Object of values
     *
     * @type {TypedOutput}
     * @memberof FormOutput
     */
    data: TypedOutput,
    /**
     * Object of confirmed values
     *
     * @type {TypedOutput}
     * @memberof FormOutput
     */
    formCompositorConfirms: {
        data: TypedOutput
    }
}
/**
 * ContentIO Module Configuration.
 * If the use of the form requires components of JUPLOAD or JAVATAR 
 * it is necessary to provide the configuration of the ContentIO Module
 *
 * @export
 * @interface ContentIOConfig
 */
export interface ContentIOConfig {

    /**
     * client axios base path
     * @example {string} ApiService.servicePath('core-contentio')
     * @type {string}
     */
    servicePath: string,
    /**
     * file middlePath service param
     * @default {string} uploads
     * @type {string}
     */
    middlePath?: string,
    /**
     * file moduleName service param
     * @default {string} jnext
     * @type {string}
     */
    moduleName?: string,
    /**
     * Defines if contentio exec public or private request
     *
     * @type {('PRIVATE' | 'PUBLIC')}
     */
    authContext?: 'PRIVATE' | 'PUBLIC',
    /**
     * List of jak headers required in client axios module
     */
    httpHeaders: Record<string, string>
}

export type OverrideColsProps = {
    [type in `${FieldDTOTypes}` | 'DEFAULT' | string]?: ColProps
}
export type RenderAfterFieldProps = {
    [type in `${FieldDTOTypes}` | string]?: {
        colProps?: ColProps
        element: JSX.Element
    }
}

export const isJDateField = (props: JFieldBaseProps): props is JDateProps => {
    return isGenericDateField(props.type)
}

export enum JTextHTMLTypes {
    COLOR = "color",
    EMAIL = "email",
    HIDDEN = "hidden",
    NUMBER = "number",
    PASSWORD = "password",
    TEL = "tel",
    TEXT = "text",
    URL = "url",
}
